/**
 * Full-fidelity, password-encrypted safe backup (export / restore).
 *
 * The existing LastPass-CSV export is a lossy interchange format — logins only,
 * plaintext, no passkeys. This module is the round-trippable native backup: it
 * serializes the caller's personal safe items **verbatim** (every field,
 * including `passkeys` with their private JWKs) and encrypts the whole payload
 * under a passphrase so the private key material never touches disk in
 * cleartext. Restore decrypts, validates, and hands the items back for re-add.
 *
 * The on-disk file is a self-describing JSON envelope (`SafeExportEnvelope`):
 * a KDF descriptor + AES-256-GCM ciphertext. Everything needed to decrypt
 * (bar the passphrase) is in the envelope, so a future KDF/iteration change
 * stays backwards-decryptable. The byte scheme is pinned by a test vector and
 * documented in `docs/architecture/07-client-contract.md`.
 *
 * Platform-agnostic: uses only Web Crypto (`crypto.subtle`) + the shared
 * base64 helpers, so it runs unchanged in the web app, the extension, and Node.
 */
import { bytesToBase64, base64ToBytes } from './encoding';
import type { VaultItem } from './index';
import type { ImportedItem } from './importers/types';

/** Magic string identifying a Hey Be Safe backup file. */
export const SAFE_EXPORT_FORMAT = 'heybesafe-safe-export';
/** Current envelope version. Bump only on an incompatible format change. */
export const SAFE_EXPORT_VERSION = 1;
/** Suggested file extension for a backup. */
export const SAFE_EXPORT_EXTENSION = 'hbsbak';

const KDF_ITERATIONS = 600_000; // matches the app's local-wrapping-key KDF
const KDF_HASH = 'SHA-256';
const SALT_BYTES = 16;
const IV_BYTES = 12; // AES-GCM standard nonce length

/** The encrypted-at-rest backup file. All binary fields are standard base64. */
export interface SafeExportEnvelope {
  format: typeof SAFE_EXPORT_FORMAT;
  version: number;
  /** ISO-8601 time the backup was produced (plaintext, for the restore UI). */
  exportedAt: string;
  kdf: { name: 'PBKDF2'; hash: typeof KDF_HASH; iterations: number; salt: string };
  cipher: 'AES-256-GCM';
  iv: string;
  /** base64(AES-GCM ciphertext || tag), as Web Crypto returns it. */
  ciphertext: string;
}

/** The plaintext payload (JSON) that gets encrypted. */
interface SafeExportPayload {
  version: number;
  exportedAt: string;
  items: VaultItem[];
}

/** Restore failed because the passphrase was wrong (or the file was tampered). */
export class WrongPassphraseError extends Error {
  constructor() {
    super('Wrong password, or the backup file has been altered.');
    this.name = 'WrongPassphraseError';
  }
}

/** The file is not a recognizable / supported Hey Be Safe backup. */
export class MalformedBackupError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'MalformedBackupError';
  }
}

/** Derive an AES-256-GCM key from the passphrase + salt via PBKDF2. */
async function deriveKey(passphrase: string, salt: Uint8Array, iterations: number): Promise<CryptoKey> {
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(passphrase),
    'PBKDF2',
    false,
    ['deriveKey'],
  );
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt: salt as BufferSource, iterations, hash: KDF_HASH },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );
}

/**
 * Encrypt a snapshot of safe items into a backup envelope (pretty-printed JSON
 * string, ready to write to a file). `passphrase` must be non-empty. Items are
 * captured verbatim — pass only the caller's OWN (personal) items; org/shared
 * items are the family's and are excluded by the caller.
 */
export async function encryptSafeExport(items: VaultItem[], passphrase: string, exportedAt: string): Promise<string> {
  if (!passphrase) throw new Error('A passphrase is required to encrypt a backup.');
  const salt = crypto.getRandomValues(new Uint8Array(SALT_BYTES));
  const iv = crypto.getRandomValues(new Uint8Array(IV_BYTES));
  const key = await deriveKey(passphrase, salt, KDF_ITERATIONS);

  const payload: SafeExportPayload = { version: SAFE_EXPORT_VERSION, exportedAt, items };
  const plaintext = new TextEncoder().encode(JSON.stringify(payload));
  const ciphertext = new Uint8Array(
    await crypto.subtle.encrypt({ name: 'AES-GCM', iv: iv as BufferSource }, key, plaintext),
  );

  const envelope: SafeExportEnvelope = {
    format: SAFE_EXPORT_FORMAT,
    version: SAFE_EXPORT_VERSION,
    exportedAt,
    kdf: { name: 'PBKDF2', hash: KDF_HASH, iterations: KDF_ITERATIONS, salt: bytesToBase64(salt) },
    cipher: 'AES-256-GCM',
    iv: bytesToBase64(iv),
    ciphertext: bytesToBase64(ciphertext),
  };
  return JSON.stringify(envelope, null, 2);
}

/** Parse + structurally validate an envelope. Throws MalformedBackupError. */
function parseEnvelope(fileText: string): SafeExportEnvelope {
  let raw: unknown;
  try {
    raw = JSON.parse(fileText);
  } catch {
    throw new MalformedBackupError("This file isn't a Hey Be Safe backup.");
  }
  const e = raw as Partial<SafeExportEnvelope>;
  if (!e || e.format !== SAFE_EXPORT_FORMAT) {
    throw new MalformedBackupError("This file isn't a Hey Be Safe backup.");
  }
  if (e.version !== SAFE_EXPORT_VERSION) {
    throw new MalformedBackupError(
      `This backup was made by a different version (v${e.version ?? '?'}); this app reads v${SAFE_EXPORT_VERSION}.`,
    );
  }
  if (
    e.cipher !== 'AES-256-GCM' || !e.iv || !e.ciphertext ||
    !e.kdf || e.kdf.name !== 'PBKDF2' || !e.kdf.salt || typeof e.kdf.iterations !== 'number'
  ) {
    throw new MalformedBackupError('This backup file is incomplete or corrupted.');
  }
  return e as SafeExportEnvelope;
}

/**
 * Decrypt a backup envelope with the passphrase. Returns the restored items and
 * the export timestamp. Throws `MalformedBackupError` for a bad/unsupported
 * file and `WrongPassphraseError` when the passphrase is wrong or the ciphertext
 * was tampered (AES-GCM auth-tag failure is indistinguishable from a bad key).
 */
export async function decryptSafeExport(
  fileText: string,
  passphrase: string,
): Promise<{ items: VaultItem[]; exportedAt: string }> {
  const envelope = parseEnvelope(fileText);
  const key = await deriveKey(passphrase, base64ToBytes(envelope.kdf.salt), envelope.kdf.iterations);

  let plaintextBytes: ArrayBuffer;
  try {
    plaintextBytes = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: base64ToBytes(envelope.iv) as BufferSource },
      key,
      base64ToBytes(envelope.ciphertext) as BufferSource,
    );
  } catch {
    throw new WrongPassphraseError();
  }

  let payload: SafeExportPayload;
  try {
    payload = JSON.parse(new TextDecoder().decode(plaintextBytes));
  } catch {
    // Decryption succeeded (right key) but the plaintext isn't our JSON — the
    // file is corrupt in a way the auth tag didn't catch (shouldn't happen).
    throw new MalformedBackupError('The backup decrypted but its contents are unreadable.');
  }
  if (!payload || !Array.isArray(payload.items)) {
    throw new MalformedBackupError('The backup decrypted but contains no items.');
  }
  return { items: payload.items, exportedAt: payload.exportedAt ?? envelope.exportedAt };
}

/**
 * Strip server/identity-assigned fields so restored items are re-added as fresh
 * items (the safe assigns new ids/timestamps). Also drops `orgId`/`circleId`:
 * a personal-safe restore must never silently re-home an item into a family the
 * user may no longer belong to — shared items are the family's, not the backup's.
 */
export function toImportedItems(items: VaultItem[]): ImportedItem[] {
  return items.map((item) => {
    const { id: _id, createdAt: _c, updatedAt: _u, orgId: _o, circleId: _ci, ...rest } = item;
    return rest;
  });
}

/** Count restored items by type, for the restore-summary UI. */
export function itemTypeCounts(items: Pick<VaultItem, 'type'>[]): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const item of items) counts[item.type] = (counts[item.type] ?? 0) + 1;
  return counts;
}

/** How many of the items carry at least one passkey (inline or legacy standalone). */
export function passkeyCount(items: VaultItem[]): number {
  return items.filter((i) => (i.passkeys && i.passkeys.length > 0) || i.type === 'passkey').length;
}
